/**
 * The control-only bridge to the native shell, declared once so both sides can be read
 * against the same names.
 *
 * This file is the **seam**. The Kotlin plugin in
 * `apps/mobile-native/android/app/src/main/java/org/ajahnbrahm/app/BrahmPlugin.kt` is the
 * other half of it, and the two are shipped together — the web build is bundled into the
 * APK or delivered as a signed, protocol-checked OTA bundle. `server.url` is absent
 * from release; incompatible OTA bundles are refused before the page is loaded.
 *
 * Two rules the shape of this interface exists to enforce:
 *
 * **No audio crosses it.** No encoded bytes, no PCM, no ArrayBuffer, in either direction.
 * The web side sends an envelope of integer frame operations; the native side downloads,
 * decodes and mixes the audio itself. A bridge that carried samples would put a JavaScript
 * thread on a 48 kHz deadline, which is the failure the native shell exists to remove.
 *
 * **The envelope crosses exactly once, in `prepare`.** After that a session is addressed
 * by the SHA-256 of its serialized envelope. The reason is a hard platform limit rather
 * than taste: an envelope may be up to 4 MiB and Android's Binder transaction buffer is
 * about 1 MiB for a whole process, so re-sending it on every command would fail at some
 * size nobody tested. `prepare` persists it through `ActiveSessionStore` (temp file, fsync,
 * atomic rename, checksummed) and hands back the digest.
 */

import { registerPlugin } from '@capacitor/core';
import { PROTOCOL_VERSION } from './native-protocol.ts';

/**
 * The version of *this interface* — the method set and the field names below.
 *
 * Deliberately separate from `PROTOCOL_VERSION`, which versions the envelope on the wire.
 * The two change for different reasons: adding `prepare` and the cache fields did not
 * change one byte of an envelope, and bumping the envelope version instead would have
 * invalidated every checked-in golden for a change no golden can see.
 */
export const BRIDGE_VERSION = 3;

export interface BrahmCapabilities {
  /** The envelope protocol the shell parses. Must equal this build's `PROTOCOL_VERSION`. */
  protocolVersion: number;
  /** The method set the shell implements. Must equal `BRIDGE_VERSION`. */
  bridgeVersion: number;
  /** A shell can speak the right protocol and still have no player in it. */
  playbackAvailable: boolean;
}

export interface PrepareRequest {
  /** `JSON.stringify` of a `NativeEnvelope`. At most `MAX_ENVELOPE_BYTES`. */
  envelopeJson: string;
  /**
   * Bytes the person has allowed the app to keep on disk, from their existing storage
   * setting. Zero means stream-only: the session's own assets are still fetched, because
   * a decoder needs a seekable file, but nothing survives the end of the sit.
   */
  budgetBytes: number;
}

export interface PrepareResult {
  /** SHA-256 of `envelopeJson`, lowercase hex. The identity every later command uses. */
  envelopeSha256: string;
  /** Total bytes this session's assets occupy, cached or not. */
  cacheTotalBytes: number;
}

export interface NativeCacheUsage { cachedBytes: number; cachedEntries: number }
export const PROFILE_REQUEST_PATH = 'path';
export interface ProfileRequest { path: string; manifestVersion: string }

export interface LoadRequest {
  envelopeSha256: string;
  /** What the notification says this is. The service has no manifest to look a name up in. */
  title: string;
  subtitle: string;
}

/**
 * `preparing` is the only state in which a `load` is expected to fail; `error` carries a
 * stable code in `PlaybackStatus.error` and is not retried automatically, because a retry
 * loop against a failing network is how a background service burns a battery in silence.
 */
export type CacheState = 'idle' | 'preparing' | 'ready' | 'error';

export interface PlaybackStatus {
  /** The output frame being heard. Frames, never seconds: see `docs/NATIVE-PROTOCOL.md`. */
  frame: number;
  /** Android elapsed-realtime timestamp taken beside `frame`, in nanoseconds. */
  observedAtElapsedRealtimeNanos: number;
  bufferedFrame: number;
  totalFrames: number;
  /** Media3 `Player.STATE_*`; see the constants below. */
  playbackState: number;
  playWhenReady: boolean;
  recordingId: string | null;
  /** A stable `E_*` code, or null. Never a message intended to be shown as-is. */
  error: string | null;
  cacheState: CacheState;
  cacheReadyBytes: number;
  cacheTotalBytes: number;
}

/** Media3 `Player.STATE_*`. Repeated here because the bridge reports the raw integer. */
export const PLAYER_STATE_IDLE = 1;
export const PLAYER_STATE_BUFFERING = 2;
export const PLAYER_STATE_READY = 3;
export const PLAYER_STATE_ENDED = 4;

export interface BrahmBridge {
  protocolVersion(): Promise<BrahmCapabilities>;
  /** Validate, persist, and begin filling the disk cache. Returns without waiting for it. */
  prepare(req: PrepareRequest): Promise<PrepareResult>;
  /**
   * Hand the service the prepared session. Refuses unless every asset is cached and
   * verified — a session that starts with a missing part would be a silent hole in speech,
   * so the refusal is the feature.
   */
  load(req: LoadRequest): Promise<void>;
  play(): Promise<void>;
  pause(): Promise<void>;
  stop(): Promise<void>;
  seekToFrame(req: { frame: number }): Promise<void>;
  status(): Promise<PlaybackStatus>;
  cacheUsage(): Promise<NativeCacheUsage>;
  clearCache(): Promise<NativeCacheUsage>;
  /** Resolve only from the verified native cache; rejects on a miss. */
  profileRms(req: ProfileRequest): Promise<{ rms: number }>;
}

export interface NativeUpdateBridge {
  // Additive lifecycle methods, independent of the audio method-set version. OTA
  // manifests require native versionCode >= 4; older debug shells may reject them.
  bundleReady(req: { build: number }): Promise<void>;
  updateContext(req: { home: boolean }): Promise<void>;
  updateStatus(): Promise<{ source: 'bundled' | 'ota'; build: number; lastCheck: number; lastError: string | null }>;
}

export const Brahm = registerPlugin<BrahmBridge & NativeUpdateBridge>('Brahm');

/**
 * Whether this build may use the native engine.
 *
 * There is deliberately no partial acceptance and no fallback. `PLAN.md`: *"Do not retain
 * a Web Audio fallback in a production native build… an implicit fallback would silently
 * weaken the promise."* A shell that answers with the wrong numbers is a build mistake,
 * and it should look like one rather than like a quieter app.
 */
export function bridgeIsCompatible(caps: BrahmCapabilities): boolean {
  return caps.protocolVersion === PROTOCOL_VERSION
    && caps.bridgeVersion === BRIDGE_VERSION
    && caps.playbackAvailable === true;
}
